import { MenuItem, useDisclosure } from "@chakra-ui/react";
import { AllocationActiveEditDrawer } from "features/allocations/edit/AllocationActiveEditDrawer";
import { AllocationEditDrawer } from "features/allocations/edit/AllocationEditDrawer";
import { AllocationEditTabProps } from "features/allocations/edit/AllocationEditTabProps";
import { useAllocationRuleRetrieveSuspense } from "gen/endpoints/allocation-rule/allocation-rule";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { useCalculationName } from "shared/hooks/useCalculationName";
import { useEntityName } from "shared/hooks/useEntityName";

export const AllocationEditTabDetails: React.FC = () => {
  const { fundFamilyId, allocationRuleId } =
    useOutletContext<AllocationEditTabProps>();
  const { t } = useTranslation(["allocation", "common"]);

  const { data: allocationRule, refetch } = useAllocationRuleRetrieveSuspense(
    fundFamilyId,
    allocationRuleId,
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const calculationName = useCalculationName(
    fundFamilyId,
    allocationRule.transaction_calculation_id,
  );
  const entityName = useEntityName(fundFamilyId, allocationRule.entity_id);

  const onCloseComplete = () => {
    onClose();
    void refetch();
  };

  return (
    <PageTabBody>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockHeader>
            <Restricted permission={PermissionTypeEnum.service}>
              <ContentBlockActions>
                <ContentBlockMenu>
                  <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
                </ContentBlockMenu>
              </ContentBlockActions>
            </Restricted>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("common:name")}>
                {allocationRule.name}
              </Descriptions.Item>
              <Descriptions.Item label={t("allocation:transactionCalculation")}>
                {calculationName}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("allocation:details.connectedEntity")}
              >
                {entityName}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      {isOpen &&
        (allocationRule.state === "active" ? (
          <AllocationActiveEditDrawer
            fundFamilyId={fundFamilyId}
            allocationRuleId={allocationRuleId}
            onCloseComplete={onCloseComplete}
          />
        ) : (
          <AllocationEditDrawer
            fundFamilyId={fundFamilyId}
            allocationRuleId={allocationRuleId}
            onCloseComplete={onCloseComplete}
          />
        ))}
    </PageTabBody>
  );
};
